import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Divider,
  Badge,
  useToast,
  Grid,
  GridItem,
  Spinner,
} from "@chakra-ui/react";
import { FiArrowLeft, FiEdit2, FiTrash2 } from "react-icons/fi";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import {
  getOnePackage,
  deleteOnePackage,
} from "../../../actions/management.action";
import { DeleteAlert } from "../../../components/DeleteAlert";

const DetailPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GB");
  };

  // Utility function to parse string data to array
  const parseStringToArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      // Try to parse as JSON first (for backend responses)
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        // Not JSON, continue with newline split
      }
      // Fall back to comma/newline split for legacy format
      return value
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const normalizeArrayForDisplay = (value) => {
    const baseArray = parseStringToArray(value);
    if (
      Array.isArray(baseArray) &&
      baseArray.length === 1 &&
      typeof baseArray[0] === "string"
    ) {
      const maybeJson = baseArray[0].trim();
      if (maybeJson.startsWith("[") && maybeJson.endsWith("]")) {
        try {
          const parsed = JSON.parse(maybeJson);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          return baseArray;
        }
      }
    }
    return baseArray;
  };

  const normalizeJourneyForDisplay = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    loadPackageDetails();
  }, [id]);

  const loadPackageDetails = async () => {
    try {
      setLoading(true);
      const response = await getOnePackage(id);
      if (response?.success) {
        setPackageData(response.data);
      } else {
        toast({
          title: "Error",
          description: "Failed to load package details",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error loading package:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to load package details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOnePackage(id);
      toast({
        title: "Success",
        description: "Package deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Close the alert and navigate after showing toast
      setIsDeleteAlertOpen(false);
      setTimeout(() => {
        navigate("/management/packages");
      }, 1000);
    } catch (error) {
      console.error("Error deleting package:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete package",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      throw error; // Re-throw so DeleteAlert can handle it
    }
  };

  if (loading) {
    return (
      <Box
        p={8}
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  //   if (!packageData) {
  //     return (
  //       <Box p={8} minH="100vh">
  //         <Button
  //           leftIcon={<FiArrowLeft />}
  //           variant="ghost"
  //           mb={4}
  //           onClick={() => navigate("/management/packages")}
  //         >
  //           Back to Packages
  //         </Button>
  //         <Text>Package not found</Text>
  //       </Box>
  //     );
  //   }

  return (
    <Box p={8} minH="100vh" bg="gray.50">
      {/* Breadcrumb */}
      <BreadCrumbMaker
        items={[
          { link: "/management", title: "Operations Setup" },
          { link: "/management/packages", title: "Packages" },
          { title: packageData?.packageTitle, on: true },
        ]}
      />

      {/* Main Container */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        {/* Header with Image */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={0}>
          {/* Image Section */}
          <GridItem>
            <Image
              src={packageData?.image}
              alt={packageData?.packageTitle}
              w="100%"
              h="400px"
              objectFit="cover"
            />
          </GridItem>

          {/* Header Info Section */}
          <GridItem
            p={6}
            bg="white"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <VStack align="start" spacing={4}>
              <VStack align="start" spacing={2}>
                <Heading fontSize="2xl" color="#1A202C">
                  {packageData?.packageTitle}
                </Heading>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="blue" fontSize="sm">
                    {packageData?.category}
                  </Badge>
                  <Badge colorScheme="purple" fontSize="sm">
                    {packageData?.packageType}
                  </Badge>
                  {packageData?.activity &&
                    String(packageData.activity).trim() !== "-" && (
                      <Badge colorScheme="green" fontSize="sm">
                        {packageData?.activity}
                      </Badge>
                    )}
                  <Badge
                    colorScheme={
                      packageData?.productStatus === "active"
                        ? "green"
                        : packageData?.productStatus === "inactive"
                          ? "red"
                          : "yellow"
                    }
                    fontSize="sm"
                    textTransform="capitalize"
                  >
                    {packageData?.productStatus || "draft"}
                  </Badge>
                  {normalizeArrayForDisplay(packageData?.tags)
                    .filter((tag) => String(tag || "").trim() !== "-")
                    .map((tag) => (
                      <Badge
                        key={tag}
                        colorScheme="purple"
                        fontSize="sm"
                        variant="subtle"
                      >
                        {tag}
                      </Badge>
                    ))}
                </HStack>
              </VStack>

              <VStack align="start" spacing={2} w="100%">
                <Box>
                  <Text fontWeight="600" fontSize="sm" color="gray.600">
                    Unique Code
                  </Text>
                  <Text fontSize="lg">{packageData?.uniqueCode}</Text>
                </Box>

                <Box>
                  <Text fontWeight="600" fontSize="sm" color="gray.600">
                    Duration
                  </Text>
                  <Text fontSize="lg">{packageData?.duration}</Text>
                </Box>

                <Box>
                  <Text fontWeight="600" fontSize="sm" color="gray.600">
                    Travellers
                  </Text>
                  <Text fontSize="lg">{packageData?.travellers || "N/A"}</Text>
                </Box>
              </VStack>
            </VStack>

            {/* Action Buttons */}
            <HStack spacing={3} pt={4} borderTop="1px" borderColor="gray.200">
              <Button
                leftIcon={<FiEdit2 />}
                colorScheme="blue"
                size="md"
                onClick={() => navigate(`/management/packages/edit/${id}`)}
              >
                Edit
              </Button>
              <Button
                leftIcon={<FiTrash2 />}
                colorScheme="red"
                variant="outline"
                size="md"
                onClick={() => setIsDeleteAlertOpen(true)}
              >
                Delete
              </Button>
            </HStack>
          </GridItem>
        </Grid>

        <Divider />

        {/* Details Section */}
        <Box p={6}>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={6}
            mb={6}
          >
            {/* Start Location */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Start Location
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.startLocation}
              </Text>
            </Box>

            {/* End Location */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                End Location
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.endLocation}
              </Text>
            </Box>

            {/* Customer Notes */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Customer Notes
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.customerNote || "N/A"}
              </Text>
            </Box>

            {/* Created Date */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Created Date
              </Text>
              <Text fontSize="md" color="#1A202C">
                {new Date(packageData?.createdAt).toLocaleDateString()}
              </Text>
            </Box>

            {/* Pricing Type */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Pricing Type
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.pricingType || "N/A"}
              </Text>
            </Box>

            {/* Minimum Travellers */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Minimum Travellers
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.minTravellers || "N/A"}
              </Text>
            </Box>

            {/* Vehicle Segment */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Vehicle Segment
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.vehicleSegment || "N/A"}
              </Text>
            </Box>

            {/* Currency */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Currency
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.currency || "INR"}
              </Text>
            </Box>

            {/* Amount Without Stay */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Amount Without Stay
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.amountWithoutStay ?? "N/A"}
              </Text>
            </Box>

            {/* Amount With Stay */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Amount With Stay
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.amountWithStay ?? "N/A"}
              </Text>
            </Box>

            {/* Promotion */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Promotion
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.promotion || "N/A"}
              </Text>
            </Box>

            {/* Promotion Dates */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Promo Dates
              </Text>
              <Text fontSize="md" color="#1A202C">
                {formatDate(packageData?.promoStartDate)} -{" "}
                {formatDate(packageData?.promoEndDate)}
              </Text>
            </Box>

            {/* Surge Price */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Surge Price
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.surgePrice ?? "N/A"}
              </Text>
            </Box>

            {/* Surge Dates */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Surge Dates
              </Text>
              <Text fontSize="md" color="#1A202C">
                {formatDate(packageData?.surgeStartDate)} -{" "}
                {formatDate(packageData?.surgeEndDate)}
              </Text>
            </Box>

            {/* Product Status */}
            <Box>
              <Text fontWeight="600" fontSize="sm" color="gray.600" mb={2}>
                Product Status
              </Text>
              <Text fontSize="md" color="#1A202C">
                {packageData?.productStatus || "draft"}
              </Text>
            </Box>
          </Grid>

          {/* Stops */}
          {packageData?.stops && packageData.stops.length > 0 && (
            <Box mb={6}>
              <Heading fontSize="lg" mb={3} color="#1A202C">
                Stops
              </Heading>
              <VStack align="start" spacing={2} pl={4}>
                {packageData.stops.map((stop, index) => (
                  <HStack key={index} spacing={2}>
                    <Badge colorScheme="gray">{index + 1}</Badge>
                    <Text>{stop}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {/* Journey / Days */}
          {normalizeJourneyForDisplay(packageData?.journey).length > 0 && (
            <Box mb={6}>
              <Heading fontSize="lg" mb={3} color="#1A202C">
                Journey Plan
              </Heading>
              <VStack align="stretch" spacing={3}>
                {normalizeJourneyForDisplay(packageData?.journey)
                  .slice()
                  .sort((a, b) => (a?.day || 0) - (b?.day || 0))
                  .map((item, index) => (
                    <Box
                      key={`${item?.day ?? index}`}
                      bg="gray.50"
                      p={4}
                      borderRadius="md"
                      borderLeft="4px"
                      borderColor="blue.400"
                    >
                      <HStack spacing={2} mb={2}>
                        <Badge colorScheme="blue">Day {item?.day}</Badge>
                        {item?.location && (
                          <Badge colorScheme="teal">{item.location}</Badge>
                        )}
                      </HStack>
                      {item?.accommodation && (
                        <Text color="gray.700" mb={2}><b>Accommodation:</b> {item.accommodation}</Text>
                      )}
                      {item?.itinerary && item.itinerary.length > 0 && (
                        <Box mb={2}>
                          <Text fontWeight="bold" color="gray.700">Itinerary:</Text>
                          <VStack align="start" spacing={1} pl={2}>
                            {item.itinerary.map((it, idx) => (
                              <Box key={idx}>
                                <Text><b>{it.attraction}</b>: {it.details}</Text>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      )}
                      {item?.foodAndDrinks && item.foodAndDrinks.length > 0 && (
                        <Box mb={2}>
                          <Text fontWeight="bold" color="gray.700">Food & Drinks:</Text>
                          <VStack align="start" spacing={1} pl={2}>
                            {item.foodAndDrinks.map((fd, idx) => (
                              <Box key={idx}>
                                <Text><b>{fd.mealType}</b>: {fd.menuDetails}</Text>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </Box>
                  ))}
              </VStack>
            </Box>
          )}

          <Divider />

          {/* Inclusions and Exclusions */}
          <Box my={6}>
            <Heading fontSize="lg" mb={3} color="#1A202C">
              Inclusions and Exclusions
            </Heading>
            <Box
              bg="gray.50"
              p={4}
              borderRadius="md"
              borderLeft="4px"
              borderColor="blue.400"
            >
              <Text whiteSpace="pre-wrap" color="#333">
                {normalizeArrayForDisplay(
                  packageData?.inclusionsAndExclusions,
                ).map((item) => `• ${item}\n`) || "N/A"}
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Cancellation Policy */}
          <Box my={6}>
            <Heading fontSize="lg" mb={3} color="#1A202C">
              Cancellation Policy
            </Heading>
            <Box
              bg="gray.50"
              p={4}
              borderRadius="md"
              borderLeft="4px"
              borderColor="red.400"
            >
              <Text whiteSpace="pre-wrap" color="#333">
                {normalizeArrayForDisplay(packageData?.cancellationPolicy).map(
                  (item) => `• ${item}\n`,
                ) || "N/A"}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Delete Alert */}
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onDelete={handleDelete}
        label="Package"
        itemName={packageData?.packageTitle}
      />
    </Box>
  );
};

export default DetailPackage;
